import React, { FC } from 'react'
import {
  Button,
  Divider,
  Panel,
  Stack,
  Placeholder,
} from 'rsuite'
import useTranslation from 'next-translate/useTranslation'

import { ReviewMinimized } from '../../../utils/types/Review'
import ReviewCard from './ReviewCard'
import Repeat from '../../Utils/Repeat'

import styles from '../../../styles/SingleProductReviewsSection.module.less'

interface ReviewsPanelProps {
  reviews: ReviewMinimized[]
  reviewTotal: number
  loadMoreReviews: () => void
  isLoading: boolean
  isLoadingInitial: boolean
}

const ReviewsPanel: FC<ReviewsPanelProps> = (props) => {
  const {
    reviews,
    reviewTotal,
    loadMoreReviews,
    isLoading,
    isLoadingInitial,
  } = props

  const isMoreReviewsAvailable = reviewTotal > reviews.length
  const { t } = useTranslation('products')

  return (
    <Panel>
      {isLoadingInitial ? (
        <Stack
          spacing={25}
          direction="column"
          alignItems="stretch"
        >
          <Placeholder.Paragraph
            rows={1}
            rowHeight={25}
            active
            className={`${styles['placeholder-reviews-section-245']} margin-bottom-spacer-half`}
          />
          <Repeat
            n={2}
            wrapper={(
              <Stack
                spacing={50}
                direction="column"
                alignItems="stretch"
              />
            )}
          >
            <Stack
              spacing={15}
              direction="column"
              alignItems="stretch"
            >
              <Stack
                spacing={10}
                direction="column"
                alignItems="stretch"
              >
                <Stack
                  spacing={10}
                  direction="row"
                >
                  <Placeholder.Paragraph
                    rows={1}
                    rowHeight={15}
                    className={styles['placeholder-reviews-section-80']}
                    active
                  />
                  <Placeholder.Paragraph
                    rows={1}
                    rowHeight={15}
                    className={styles['placeholder-reviews-section-190']}
                    active
                  />
                </Stack>
                <Placeholder.Paragraph
                  rows={1}
                  rowHeight={15}
                  className={styles['placeholder-reviews-section-280']}
                  active
                />
              </Stack>
              <Stack
                spacing={10}
                direction="column"
                alignItems="stretch"
              >
                <Placeholder.Paragraph
                  rows={1}
                  rowHeight={15}
                  active
                  className={styles['placeholder-reviews-section-full']}
                />
                <Placeholder.Paragraph
                  rows={1}
                  rowHeight={15}
                  className={styles['placeholder-reviews-section-full']}
                  active
                />
              </Stack>
              <Repeat
                n={4}
                wrapper={(
                  <Stack
                    spacing={10}
                    direction="row"
                  />
                )}
              >
                <Placeholder.Graph
                  width={140}
                  height={80}
                  active
                />
              </Repeat>
            </Stack>
          </Repeat>
          <Stack
            spacing={30}
            direction="column"
            alignItems="stretch"
          >
            <Placeholder.Paragraph
              rows={1}
              rowHeight={15}
              className={styles['placeholder-reviews-section-140']}
              active
            />
            <Placeholder.Graph
              width={180}
              height={40}
              active
            />
          </Stack>
        </Stack>
      ) : (
        <Stack
          spacing={40}
          direction="column"
          alignItems="stretch"
        >
          <p className={`h2 ${styles['reviews-panel-header']}`}>
            {t('{{ count }} reviews', { count: reviewTotal })}
          </p>
          <Stack
            spacing={15}
            direction="column"
            alignItems="stretch"
            divider={<Divider />}
          >
            {!!reviews.length && (
              reviews.map((review) => (
                <ReviewCard
                  review={review}
                  key={review.id}
                />
              ))
            )}
          </Stack>
          {isMoreReviewsAvailable && (
            <Button
              onClick={loadMoreReviews}
              loading={isLoading}
              appearance="ghost"
            >
              {t('Show more reviews')}
            </Button>
          )}
        </Stack>
      )}
    </Panel>
  )
}

export default ReviewsPanel
